import { useMemo, type CSSProperties } from 'react';
import type { WbsConstraintsDoc, WbsItemDoc, WbsItemKind } from '@mct/shared';
import { cn } from '../../lib/utils';
import { getWbsBarColors } from '../../lib/wbsBarColors';
import { classifyDelay } from '../../lib/wbsTree';
import {
  ZOOM_DAY_WIDTH,
  addDays,
  barRect,
  chartPixelWidth,
  dayToX,
  diffDays,
  startOfDayUTC,
  tsToDate,
  type GanttZoom,
} from '../../lib/wbsGantt';

export const ROW_HEIGHT = 32;
const HEADER_HEIGHT = 48;
const BAR_MARGIN_Y = 6;

/**
 * Vertical gap inserted ABOVE every phase row (except the very first
 * row), in pixels. Lifted to module scope so the tree pane can import
 * it and apply the same marginTop, keeping both panes' rows aligned.
 */
export const PHASE_GAP_PX = 12;

/**
 * Trailing breathing room appended after the last row so the bottom
 * task isn't flush against the scroll edge. Same value applied on both
 * the tree pane and the Gantt pane to keep heights in lockstep.
 */
export const BOTTOM_PAD_PX = ROW_HEIGHT;

/**
 * Cumulative top-edge Y position for each row in the same order as
 * `items`. The first row sits at 0; each subsequent row sits at
 * `prevTop + ROW_HEIGHT (+ PHASE_GAP_PX if this row is a phase)`. Both
 * the tree (via marginTop on phase rows) and the gantt (via absolute
 * positioning) consume this so the section gap appears in both panes.
 */
export function computeRowTops(
  items: readonly { kind: WbsItemKind }[],
): number[] {
  const out = new Array<number>(items.length);
  let y = 0;
  for (let i = 0; i < items.length; i++) {
    if (i > 0 && items[i]!.kind === 'phase') y += PHASE_GAP_PX;
    out[i] = y;
    y += ROW_HEIGHT;
  }
  return out;
}

interface GanttRow {
  itemId: string;
  item: WbsItemDoc;
  delayed: boolean;
}

interface Props {
  items: readonly WbsItemDoc[];
  visibleItems: readonly WbsItemDoc[];
  constraints: WbsConstraintsDoc | null;
  range: { start: Date; end: Date };
  zoom: GanttZoom;
  selectedId: string | null;
  hoveredId?: string | null;
  now: Date;
  onSelect: (id: string) => void;
  onHover?: (id: string | null) => void;
}

export function WbsGantt({
  items,
  visibleItems,
  constraints,
  range,
  zoom,
  selectedId,
  hoveredId,
  now,
  onSelect,
  onHover,
}: Props) {
  const dayWidth = ZOOM_DAY_WIDTH[zoom];
  const totalWidth = chartPixelWidth(range.start, range.end, zoom);
  // Per-row Y positions account for the phase-section gap; total height
  // matches whatever the last row's bottom edge sits at.
  const rowTops = useMemo(() => computeRowTops(visibleItems), [visibleItems]);
  const totalHeight =
    HEADER_HEIGHT +
    (rowTops.length === 0 ? 0 : rowTops[rowTops.length - 1]! + ROW_HEIGHT) +
    (rowTops.length === 0 ? 0 : BOTTOM_PAD_PX);

  const rows: GanttRow[] = useMemo(
    () =>
      visibleItems.map((item) => ({
        itemId: item.itemId,
        item,
        delayed: classifyDelay(item, now),
      })),
    [visibleItems, now],
  );

  const rowIndexById = useMemo(() => {
    const m = new Map<string, number>();
    rows.forEach((r, i) => m.set(r.itemId, i));
    return m;
  }, [rows]);

  const itemById = useMemo(() => {
    const m = new Map<string, WbsItemDoc>();
    for (const it of items) m.set(it.itemId, it);
    return m;
  }, [items]);

  const holidayBands = useMemo(() => {
    if (!constraints) return [];
    const bands: { x: number; width: number; name: string }[] = [];
    for (const h of constraints.holidays) {
      const s = tsToDate(h.start);
      const e = tsToDate(h.end);
      if (!s || !e) continue;
      if (e < range.start || s > range.end) continue;
      const clampedStart = s < range.start ? range.start : s;
      const clampedEnd = e > range.end ? range.end : e;
      const rect = barRect(clampedStart, clampedEnd, range.start, zoom);
      bands.push({ x: rect.x, width: rect.width, name: h.name });
    }
    return bands;
  }, [constraints, range, zoom]);

  /**
   * Saturday + Sunday bands at day zoom. Suppressed at week/month
   * because per-day stripes turn into visual noise once each tick is
   * <10px wide. We collapse adjacent weekend days into a single band
   * (Sat+Sun → 2-day span) to keep the DOM count low.
   */
  const weekendBands = useMemo(() => {
    if (zoom !== 'day') return [] as { x: number; width: number }[];
    const out: { x: number; width: number }[] = [];
    const totalDays = diffDays(range.start, range.end) + 1;
    for (let i = 0; i < totalDays; i++) {
      const d = addDays(range.start, i);
      const dow = d.getUTCDay();
      if (dow !== 0 && dow !== 6) continue;
      // Extend an existing run if the previous band ends right where
      // this day starts (Sat → Sun = continuous weekend).
      const last = out[out.length - 1];
      const x = i * dayWidth;
      if (last && last.x + last.width === x) {
        last.width += dayWidth;
      } else {
        out.push({ x, width: dayWidth });
      }
    }
    return out;
  }, [range.start, range.end, zoom, dayWidth]);

  const todayX = useMemo(() => {
    const today = startOfDayUTC(now);
    if (today < range.start || today > range.end) return null;
    return dayToX(today, range.start, zoom);
  }, [now, range, zoom]);

  const axis = useMemo(
    () => buildAxis(range.start, range.end, zoom),
    [range, zoom],
  );

  const dependencyArrows = useMemo(() => {
    const arrows: {
      id: string;
      from: { x: number; y: number };
      to: { x: number; y: number };
    }[] = [];
    for (const row of rows) {
      const childIdx = rowIndexById.get(row.itemId);
      if (childIdx === undefined) continue;
      const childStart = tsToDate(row.item.plannedStart);
      if (!childStart) continue;
      for (const predId of row.item.dependsOn) {
        const predIdx = rowIndexById.get(predId);
        const pred = itemById.get(predId);
        if (predIdx === undefined || !pred) continue;
        const predEnd = tsToDate(pred.plannedEnd);
        if (!predEnd) continue;
        const predX =
          dayToX(addDays(predEnd, 1), range.start, zoom);
        const childX = dayToX(childStart, range.start, zoom);
        arrows.push({
          id: `${predId}->${row.itemId}`,
          from: {
            x: predX,
            y: HEADER_HEIGHT + rowTops[predIdx]! + ROW_HEIGHT / 2,
          },
          to: {
            x: childX,
            y: HEADER_HEIGHT + rowTops[childIdx]! + ROW_HEIGHT / 2,
          },
        });
      }
    }
    return arrows;
  }, [rows, rowIndexById, itemById, range.start, zoom, rowTops]);

  return (
    <div
      className="relative"
      style={{ width: totalWidth, height: totalHeight }}
    >
      <div
        className="relative"
        style={{ width: totalWidth, height: totalHeight }}
      >
        {/* Header axis */}
        <div
          className="sticky top-0 z-20 border-b border-slate-200 bg-white"
          style={{ height: HEADER_HEIGHT }}
        >
          <AxisHeader axis={axis} dayWidth={dayWidth} zoom={zoom} />
        </div>

        {/* Weekend bands (day-zoom only). Renders below holidays so a
            registered holiday that falls on Sat/Sun still wins the
            visual weight. */}
        {weekendBands.map((b, i) => (
          <div
            key={`we-${i}`}
            aria-hidden
            className="pointer-events-none absolute bg-slate-200/50"
            style={{
              left: b.x,
              width: b.width,
              top: HEADER_HEIGHT,
              height: totalHeight - HEADER_HEIGHT,
            }}
          />
        ))}

        {/* Holidays */}
        {holidayBands.map((b, i) => (
          <div
            key={`hol-${i}`}
            title={b.name}
            className="pointer-events-none absolute top-0 bg-amber-100/40"
            style={{
              left: b.x,
              width: b.width,
              top: HEADER_HEIGHT,
              bottom: 0,
              position: 'absolute',
              height: totalHeight - HEADER_HEIGHT,
            }}
          />
        ))}

        {/* Row separators + hover/select highlight */}
        {rows.map((r, idx) => {
          const isPhase = r.item.kind === 'phase';
          return (
            <div
              key={`sep-${r.itemId}`}
              className={cn(
                'absolute left-0 right-0 border-b border-slate-100',
                // Mirror the tree-side phase styling so the section
                // start-line and tint run continuously across both panes.
                isPhase ? 'border-t-2 border-t-slate-200' : '',
                selectedId === r.itemId
                  ? 'bg-slate-200/70'
                  : hoveredId === r.itemId
                  ? isPhase
                    ? 'bg-slate-200/70'
                    : 'bg-slate-50'
                  : isPhase
                  ? 'bg-slate-100/70'
                  : '',
              )}
              style={{
                top: HEADER_HEIGHT + rowTops[idx]!,
                height: ROW_HEIGHT,
                width: totalWidth,
              }}
              onMouseEnter={
                onHover ? () => onHover(r.itemId) : undefined
              }
              onMouseLeave={onHover ? () => onHover(null) : undefined}
            />
          );
        })}

        {/* Today line */}
        {todayX != null ? (
          <div
            className="pointer-events-none absolute top-0 z-10 border-l-2 border-rose-500"
            style={{ left: todayX, height: totalHeight }}
            aria-label="今日"
          />
        ) : null}

        {/* Dependency arrows */}
        <svg
          className="pointer-events-none absolute inset-0 z-10"
          width={totalWidth}
          height={totalHeight}
        >
          <defs>
            <marker
              id="wbs-arrow"
              viewBox="0 0 10 10"
              refX="8"
              refY="5"
              markerWidth="6"
              markerHeight="6"
              orient="auto-start-reverse"
            >
              <path d="M0,0 L10,5 L0,10 Z" fill="#64748b" />
            </marker>
          </defs>
          {dependencyArrows.map((a) => {
            const mid = (a.from.x + a.to.x) / 2;
            const d = `M ${a.from.x} ${a.from.y} C ${mid} ${a.from.y}, ${mid} ${a.to.y}, ${a.to.x} ${a.to.y}`;
            return (
              <path
                key={a.id}
                d={d}
                stroke="#64748b"
                strokeWidth={1.5}
                fill="none"
                markerEnd="url(#wbs-arrow)"
              />
            );
          })}
        </svg>

        {/* Bars */}
        {rows.map((r, idx) => {
          const start = tsToDate(r.item.plannedStart);
          const end = tsToDate(r.item.plannedEnd);
          // Milestones short-circuit the duration-bar codepath: a single
          // date renders as a vertical line + ▼ + vertical title chip.
          // We anchor the marker to the row, so this lives next to the
          // bars rather than as a separate pass.
          if (r.item.kind === 'milestone') {
            if (!start) return null;
            const x = dayToX(start, range.start, zoom);
            const top = HEADER_HEIGHT + rowTops[idx]!;
            const isSelected = selectedId === r.itemId;
            return (
              <button
                key={`ms-${r.itemId}`}
                type="button"
                onClick={() => onSelect(r.itemId)}
                onMouseEnter={
                  onHover ? () => onHover(r.itemId) : undefined
                }
                onMouseLeave={onHover ? () => onHover(null) : undefined}
                className={cn(
                  'absolute z-10 flex flex-col items-center pt-0.5',
                  'text-amber-700 hover:text-amber-900',
                )}
                // 24px-wide hit-test column centered on the date so the
                // ▼ + label are easy to grab without expanding the
                // bounding box past adjacent days.
                style={{
                  left: x - 12,
                  top,
                  width: 24,
                  height: ROW_HEIGHT,
                }}
                title={`${r.item.name}\n${fmtDate(start)}`}
              >
                {/*
                 * Vertical title chip on top. `writing-mode: vertical-rl`
                 * stacks Japanese characters top→bottom naturally without
                 * a transform-rotate, which would also tilt the baseline.
                 * Hidden when the row is too narrow to read (long titles
                 * just truncate via overflow).
                 */}
                <span
                  className={cn(
                    'max-h-3 overflow-hidden whitespace-nowrap text-[9px] font-medium leading-none',
                    isSelected ? 'text-amber-900' : '',
                  )}
                  style={{ writingMode: 'vertical-rl' }}
                >
                  {r.item.name}
                </span>
                <span className="-mt-0.5 text-[10px] leading-none">▼</span>
                <span
                  className={cn(
                    'mt-0 flex-1 w-0.5',
                    isSelected ? 'bg-amber-600' : 'bg-amber-500',
                  )}
                  aria-hidden
                />
              </button>
            );
          }
          if (!start || !end) return null;
          const rect = barRect(start, end, range.start, zoom);
          const top = HEADER_HEIGHT + rowTops[idx]! + BAR_MARGIN_Y;
          const barStyle: CSSProperties = {
            left: rect.x,
            width: rect.width,
            top,
            height: ROW_HEIGHT - BAR_MARGIN_Y * 2,
          };
          const progress = Math.max(0, Math.min(100, r.item.progress));
          const actualStart = tsToDate(r.item.actualStart);
          const actualEnd = tsToDate(r.item.actualEnd);
          const actualRect =
            actualStart && actualEnd
              ? barRect(actualStart, actualEnd, range.start, zoom)
              : null;
          // Bar fill/border/label are driven by status, with `delayed`
          // (computed by classifyDelay) overriding to the overdue red.
          const colors = getWbsBarColors(r.item.status, r.delayed);
          return (
            <button
              key={`bar-${r.itemId}`}
              type="button"
              onClick={() => onSelect(r.itemId)}
              onMouseEnter={
                onHover ? () => onHover(r.itemId) : undefined
              }
              onMouseLeave={onHover ? () => onHover(null) : undefined}
              className={cn(
                'absolute z-10 overflow-hidden rounded border text-left transition-colors',
                colors.bg,
                colors.border,
                colors.striped ? 'bg-stripe' : '',
                selectedId === r.itemId ? 'ring-2 ring-slate-900' : '',
              )}
              style={barStyle}
              title={`${r.item.name}\n${fmtRange(start, end)}\n進捗 ${progress}%`}
            >
              {/* Progress overlay — a status-agnostic darkening so the
                  filled portion reads against any status colour. */}
              <div
                className="absolute inset-y-0 left-0 bg-slate-900/20"
                style={{ width: `${progress}%` }}
              />
              {actualRect ? (
                <div
                  className="absolute border-l-2 border-emerald-600"
                  style={{
                    left: actualRect.x - rect.x,
                    width: actualRect.width,
                    top: (ROW_HEIGHT - BAR_MARGIN_Y * 2) - 4,
                    height: 4,
                    backgroundColor: 'rgb(5 150 105 / 0.8)',
                  }}
                />
              ) : null}
              <span
                className={cn(
                  'relative z-10 block truncate px-1.5 text-[11px] leading-5',
                  colors.text,
                )}
              >
                {r.item.name}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function fmtRange(a: Date, b: Date): string {
  return `${fmtDate(a)} - ${fmtDate(b)}`;
}

function fmtDate(d: Date): string {
  return `${d.getUTCFullYear()}/${String(d.getUTCMonth() + 1).padStart(2, '0')}/${String(d.getUTCDate()).padStart(2, '0')}`;
}

type AxisTick = {
  /** left px */
  x: number;
  /** width in px */
  width: number;
  label: string;
};

function buildAxis(start: Date, end: Date, zoom: GanttZoom): {
  major: AxisTick[];
  minor: AxisTick[];
} {
  const dayWidth = ZOOM_DAY_WIDTH[zoom];
  const totalDays = diffDays(start, end) + 1;
  const minor: AxisTick[] = [];
  const major: AxisTick[] = [];

  if (zoom === 'day') {
    for (let i = 0; i < totalDays; i++) {
      const d = addDays(start, i);
      minor.push({
        x: i * dayWidth,
        width: dayWidth,
        label: String(d.getUTCDate()),
      });
    }
    // Month headers
    let i = 0;
    while (i < totalDays) {
      const d = addDays(start, i);
      const monthStart = i;
      while (i < totalDays) {
        const nd = addDays(start, i);
        if (nd.getUTCMonth() !== d.getUTCMonth()) break;
        i += 1;
      }
      major.push({
        x: monthStart * dayWidth,
        width: (i - monthStart) * dayWidth,
        label: `${d.getUTCFullYear()}/${d.getUTCMonth() + 1}`,
      });
    }
  } else if (zoom === 'week') {
    for (let i = 0; i < totalDays; i += 7) {
      const d = addDays(start, i);
      const w = Math.min(7, totalDays - i);
      minor.push({
        x: i * dayWidth,
        width: w * dayWidth,
        label: `${d.getUTCMonth() + 1}/${d.getUTCDate()}`,
      });
    }
    let i = 0;
    while (i < totalDays) {
      const d = addDays(start, i);
      const monthStart = i;
      while (i < totalDays) {
        const nd = addDays(start, i);
        if (nd.getUTCMonth() !== d.getUTCMonth()) break;
        i += 1;
      }
      major.push({
        x: monthStart * dayWidth,
        width: (i - monthStart) * dayWidth,
        label: `${d.getUTCFullYear()}/${d.getUTCMonth() + 1}`,
      });
    }
  } else {
    let i = 0;
    while (i < totalDays) {
      const d = addDays(start, i);
      const monthStart = i;
      while (i < totalDays) {
        const nd = addDays(start, i);
        if (nd.getUTCMonth() !== d.getUTCMonth()) break;
        i += 1;
      }
      minor.push({
        x: monthStart * dayWidth,
        width: (i - monthStart) * dayWidth,
        label: `${d.getUTCMonth() + 1}月`,
      });
      major.push({
        x: monthStart * dayWidth,
        width: (i - monthStart) * dayWidth,
        label: `${d.getUTCFullYear()}`,
      });
    }
  }

  return { major, minor };
}

function AxisHeader({
  axis,
  dayWidth,
  zoom,
}: {
  axis: { major: AxisTick[]; minor: AxisTick[] };
  dayWidth: number;
  zoom: GanttZoom;
}) {
  void dayWidth;
  void zoom;
  return (
    <>
      <div className="relative h-6 border-b border-slate-100">
        {axis.major.map((t, i) => (
          <div
            key={`maj-${i}`}
            className="absolute top-0 flex h-6 items-center justify-start border-l border-slate-200 pl-1 text-[11px] font-medium text-slate-700"
            style={{ left: t.x, width: t.width }}
          >
            {t.label}
          </div>
        ))}
      </div>
      <div className="relative h-6">
        {axis.minor.map((t, i) => (
          <div
            key={`min-${i}`}
            className="absolute top-0 flex h-6 items-center justify-start border-l border-slate-100 pl-1 text-[10px] text-slate-500"
            style={{ left: t.x, width: t.width }}
          >
            {t.label}
          </div>
        ))}
      </div>
    </>
  );
}
